import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApiBasicAuthCredential } from '../../models/appcloud/api-basic-auth-credential.model';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { HttpHeaderConstants } from '../../services/http-header.constant';
import { SpinnerService } from '../../services/spinner';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ApiBasicAuthCredentialService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private CREDENTIAL_URL: string = environment.publicApiHost + '/appcloud/api-basic-auth-credentials';

    create(credential: ApiBasicAuthCredential, additionalHeaders: any = {}): Observable<ApiBasicAuthCredential> {
        this.#spinnerService.addProcess(
            'ApiBasicAuthCredentialService.create',
            'Creating your basic authentication credential...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<ApiBasicAuthCredential>(this.CREDENTIAL_URL, credential, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating your basic authentication credential. Please try again later.'
                );
                return of<ApiBasicAuthCredential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiBasicAuthCredentialService.create'))
        );
    }

    update(
        credentialId: number,
        credential: ApiBasicAuthCredential,
        additionalHeaders: any = {}
    ): Observable<ApiBasicAuthCredential> {
        this.#spinnerService.addProcess(
            'ApiBasicAuthCredentialService.update',
            'Updating your basic authentication credential...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ApiBasicAuthCredential>(`${this.CREDENTIAL_URL}/${credentialId}`, credential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your basic authentication credential. Please try again later.'
                    );
                    return of<ApiBasicAuthCredential>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ApiBasicAuthCredentialService.update'))
            );
    }

    get(credentialId: number, additionalHeaders: any = {}): Observable<ApiBasicAuthCredential> {
        this.#spinnerService.addProcess('ApiBasicAuthCredentialService.get', 'Retrieving credential data...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<ApiBasicAuthCredential>(`${this.CREDENTIAL_URL}/${credentialId}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while fetching your basic authentication credential. Please try again later.'
                );
                return of<ApiBasicAuthCredential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiBasicAuthCredentialService.get'))
        );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess(
            'ApiBasicAuthCredentialService.delete.' + id,
            'Deleting your api basic auth credential...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.delete<Observable<any>>(`${this.CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your basic auth credential. Try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiBasicAuthCredentialService.delete.' + id))
        );
    }
}
